import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { ExteriorVerticalShadesBody } from "@/components/ExteriorVerticalShadesBody";
import hero from "@/assets/solair-pro-exterior-vertical-shade-patio.jpg";

export const Route = createFileRoute("/vertical-shades-lp")({
  validateSearch: (search: Record<string, unknown>) => ({
    key: typeof search.key === "string" ? search.key : undefined,
  }),
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: [
      ...routeMeta(ctx, {
        title: "Solair® Pro Exterior Vertical Shades — Free Texas Quote",
        description:
          "Authorized Solair® Pro dealer. Motorized exterior drop screens with side tracks for San Antonio, Austin & the Hill Country. Get a free quote in minutes.",
        image: hero,
        imageAlt: "Solair® Pro exterior vertical drop screen lowered over a Texas patio",
        socialTitle: "Solair® Pro Exterior Vertical Shades — Free Texas Quote",
        socialDescription:
          "Motorized exterior drop screens with side tracks. Block sun, wind & bugs. Free quote from Texas Made Shade — Authorized Solair® Pro Dealer.",
        keywords: "solair pro, exterior vertical shades, motorized drop screens texas",
      }),
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: LandingPage,
});

// Review token — anyone with this URL param can view the page.
// Rotate the token here to revoke access.
const REVIEW_TOKEN = "tms-review-2026-vs-lp-9f4c";

function LandingPage() {
  const { key } = Route.useSearch();
  const authorized = key === REVIEW_TOKEN;

  const containerRef = useRef<HTMLDivElement>(null);

  // Reviewer preview: neutralize every link/button so the reviewer can see
  // the full page content but cannot navigate away or submit any form.
  useEffect(() => {
    if (!authorized) return;
    const root = containerRef.current;
    if (!root) return;

    const swallow = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const el = target.closest("a, button, [role='button'], input[type='submit'], input[type='button']");
      if (el && root.contains(el)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    const onSubmit = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    root.addEventListener("click", swallow, true);
    root.addEventListener("submit", onSubmit, true);
    return () => {
      root.removeEventListener("click", swallow, true);
      root.removeEventListener("submit", onSubmit, true);
    };
  }, [authorized]);

  if (!authorized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-6 text-center text-navy-deep">
        <div className="max-w-md">
          <h1 className="font-display text-2xl font-bold uppercase">Access Restricted</h1>
          <p className="mt-3 text-sm text-navy-deep/80">
            This page is a private review preview and is not publicly available. If you were sent
            a review link, please open the full URL you received (including the access key).
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="min-h-screen bg-cream text-navy-deep">
      {/* No SiteHeader / SiteFooter / breadcrumbs — reviewer-only snapshot of the full page body */}
      <ExteriorVerticalShadesBody showBreadcrumbs={false} />
    </div>
  );
}
